/** Core */
import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import {
  CommonMFE,
  Helpers,
  MFE_NAMES,
  Providers,
  ReactMFE,
} from "@tpd-web-common-libs/nodejs-library";
/** Style */
import {
  ContainerDiv,
  SocialBanner,
  TpdCompany,
  TpdInfo,
  TpdList,
  TpdListImage,
  TpdMainList,
  TpdTitleImage,
} from "./App.style";
import styled from "@emotion/styled";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
/** Types */
import { FooterData, ICON_LIST, InfoLink, LinkType } from "./App.types";

type TAppProps = ReactMFE.Types.TRemoteReactMFEProps;

function filterDataAttributesFrom(generalObj = {}) {
  return Object.entries(generalObj).reduce((acc, [key, value]) => {
    return !!key.match("^data-")
      ? {
          ...acc,
          [key]: value,
        }
      : acc;
  }, {});
}

/** Map and enrich utility data */
const parseConfig = (config: any) => {
  return config && Object.keys(config).length > 0
    ? {
        ...config,
        socialLinks: (config?.socialLinks || []).map((link) => {
          return {
            ...link,
            className: ICON_LIST?.find((icon) => icon.name === link.name)
              ?.className,
          };
        }),
        infoLinks: (config?.infoLinks || []).map((link, index) => {
          return {
            ...link,
            index: `footer-element-list-${index}`,
            expanded: true,
          };
        }),
      }
    : config;
};

const App = ({
  __host__,
  config: INITIAL_CONFIG,
  id: containerID,
}: TAppProps) => {
  const [config, setConfig] = useState<FooterData>(parseConfig(INITIAL_CONFIG));
  const [visible, setVisible] = useState(false);
  const [stickyInPage, setStickyInPage] = useState(false);
  const companyDataRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!config) {
      const cmsProxyService = Providers.CMSProxyService.getInstance();
      cmsProxyService
        .getFooter(__host__.url)
        .then((newConfig: any) => setConfig(parseConfig(newConfig)));
    }
  }, []);

  // add margin-botton if tpdPreventivatoreStickyContainer is in page
  useEffect(() => {
    if (Helpers.EnvironmentHelper.isClientSide()) {
      setStickyInPage(
        !!document.body.querySelector(
          "[data-mfe-name=" + MFE_NAMES.tpdPreventivatoreStickyContainer + "]"
        )
      );
    }
  }, [__host__.url]);

  useEffect(() => {
    if (visible) {
      const el = companyDataRef.current;

      if (!el) {
        return;
      }

      const height = el.getBoundingClientRect().height;

      window.scrollTo({
        top: window.scrollY + height,
        behavior: "instant",
      });
    }
  }, [visible]);

  /**
   * Manage the Open/Close of a specific list item that has "collapsible" array field
   * @param item
   */
  function onClickListItem(item: InfoLink) {
    const currentItem: InfoLink = { ...item, expanded: !item.expanded };
    const index = config.infoLinks.findIndex(
      (el) => el.index === currentItem.index
    );
    const newInfoList = [...config.infoLinks];
    newInfoList.splice(index, 1, currentItem);
    setConfig({ ...config, infoLinks: newInfoList });
  }

  useEffect(() => {
    setConfig(parseConfig(INITIAL_CONFIG));
    return () => {
      // do nothing
    };
  }, [INITIAL_CONFIG]);

  return (
    config && (
      <ContainerDiv
        className={classNames({
          "footer-with-sticky-space": stickyInPage,
          hidden: !(config.configFooter && config.configFooter.visible),
        })}
      >
        {config.configFooter &&
          config.configFooter.socialLinks &&
          config.socialLinks && (
            <SocialBanner>
              <span>Seguici su</span>
              <div className="social-list">
                {config.socialLinks.map((socialLink, index) => (
                  <a
                    key={CommonMFE.generateID(
                      containerID,
                      "footer-icon",
                      index
                    )}
                    target={
                      socialLink.target === LinkType.INTERNAL
                        ? "_self"
                        : "_blank"
                    }
                    href={Helpers.RouterHelper.normalizeHref(socialLink.url)}
                    title={socialLink.name}
                    className={socialLink.className}
                    rel="noreferer, ,noopener"
                  ></a>
                ))}
              </div>
            </SocialBanner>
          )}
        {config.configFooter &&
          config.configFooter.privacyLinks &&
          config.privacyLinks && (
            <TpdMainList>
              {config.privacyLinks.map((privacyLink, index) => (
                <div
                  key={CommonMFE.generateID(
                    containerID,
                    "footer-main-list",
                    index
                  )}
                  className="list-item"
                >
                  <a
                    href={Helpers.RouterHelper.normalizeHref(privacyLink.url)}
                    title={privacyLink.name}
                    target={
                      privacyLink.target === LinkType.INTERNAL
                        ? "_self"
                        : "_blank"
                    }
                    {...filterDataAttributesFrom(privacyLink.properties)}
                  >
                    {!!privacyLink.icon &&
                    privacyLink.icon.match(/\.(png|svg|jpeg|jpg)$/i) ? (
                      <img
                        src={privacyLink.icon}
                        className={`privacy-link-img-${privacyLink.name.toLowerCase()}`}
                        alt={privacyLink.name.toUpperCase()}
                        loading="lazy"
                      ></img>
                    ) : (
                      <span>{privacyLink.name.toUpperCase()}</span>
                    )}
                  </a>
                </div>
              ))}
            </TpdMainList>
          )}
        <TpdListImage>
          <TpdList>
            {config.configFooter &&
              config.configFooter.infoLinks &&
              config.infoLinks && (
                <div className="tpd-list-items">
                  {config.infoLinks.map((infoLink, index) => (
                    <div
                      key={CommonMFE.generateID(
                        containerID,
                        "footer-simple-list",
                        index
                      )}
                      className="list-item"
                    >
                      <div className="collapsible">
                        <a
                          href={Helpers.RouterHelper.normalizeHref(
                            infoLink.url
                          )}
                          title={infoLink.name}
                        >
                          <span>{infoLink.name}</span>
                        </a>
                        {!!infoLink.collapsible && (
                          <button
                            aria-label="Expand"
                            id={`expand-${index}`}
                            className={`tpd-expand iconfont ${
                              infoLink.expanded ? "icon-Piu" : "icon-Meno"
                            }`}
                            onClick={() => onClickListItem(infoLink)}
                          ></button>
                        )}
                      </div>
                      {!!infoLink.collapsible &&
                        infoLink.collapsible.map(
                          (collapsedItem, collapsedIndex) => (
                            <div
                              key={CommonMFE.generateID(
                                containerID,
                                "footer-element-list-collapsed",
                                collapsedIndex
                              )}
                              className={`content ${
                                infoLink.expanded ? "hidden" : "visible"
                              }`}
                            >
                              <a
                                href={Helpers.RouterHelper.normalizeHref(
                                  collapsedItem.url
                                )}
                                title={collapsedItem.name}
                              >
                                <span>{collapsedItem.name}</span>
                              </a>
                            </div>
                          )
                        )}
                    </div>
                  ))}
                </div>
              )}
            <TpdCompany>
              <div className="tpd-contacts">
                <div className="tpd-contacts-content">
                  <p className="tpd-contacts-title">
                    <b>Unipol Assicurazioni S.p.A</b>
                  </p>
                  <p>Sede Legale: Via Stalingrado, 45 40128 Bologna</p>
                  <p>
                    Telefono: +39 051 5076111 - Fax: +39 051 5076666 - Mail Pec:
                    unipol@pec.unipol.it
                  </p>
                  <p>
                    <a href="/servizio-clienti">Assistenza e Supporto</a>
                  </p>
                  <p>
                    Capitale sociale i.v. € 3.365.292.408,03 - Registro delle
                    Imprese di Bologna
                  </p>
                  <p>C.F. 00284160371 - P.IVA 03740811207 - R.E.A. 160304</p>
                  <p className="small">
                    Società iscritta all'Albo Imprese di Assicurazione e
                    Riassicurazione Sez. I al numero 1.00183 Capogruppo del
                    Gruppo Assicurativo Unipol iscritto all'Albo delle società
                    capogruppo al n. 046
                  </p>
                  <p>
                    <a href="https://www.unipol.com">unipol.com</a> - <a href="https://www.unipol.it">unipol.it</a>
                  </p>
                </div>
              </div>
            </TpdCompany>
          </TpdList>
          {config.configFooter &&
            config.configFooter.appLinks &&
            config.appLinks &&
            config.appLinks.links && (
              <TpdTitleImage>
                <div className="tpd-apps-image">
                  <div className="tpd-apps">
                    <div className="tpd-title">
                      <a
                        href="/app"
                        aria-label={`Scarica o aggiorna l'App ${Helpers.BrandNameHelper.brandName()}`}
                      >
                        <span>
                          Scarica o aggiorna l'App{" "}
                          {Helpers.BrandNameHelper.brandName()}
                        </span>
                      </a>
                    </div>
                    {config.appLinks.links.map((appLink) => (
                      <a
                        key={appLink.id}
                        href={Helpers.RouterHelper.normalizeHref(appLink.url)}
                        target={
                          appLink.target === LinkType.INTERNAL
                            ? "_self"
                            : "_blank"
                        }
                      >
                        <img
                          src={appLink.icon}
                          className={`img-responsive bk_015-img-dlbtn style-${appLink.name.replace(
                            " ",
                            "-"
                          )}`}
                          alt={appLink.name}
                          loading="lazy"
                        />
                      </a>
                    ))}
                    <img
                      src="/NextAssets/images/qrcode-app.png"
                      width="155"
                      className="img-responsive"
                      alt="QRCode App"
                      loading="lazy"
                    />
                  </div>
                  <div className="tpd-image">
                    <a
                      href="/app"
                      aria-label={`${Helpers.BrandNameHelper.brandName()} App`}
                    >
                      <img
                        src="/NextAssets/images/app_footer.webp"
                        width="212"
                        className="img-responsive"
                        alt="App Footer"
                        loading="lazy"
                      />
                    </a>
                  </div>
                </div>
              </TpdTitleImage>
            )}
        </TpdListImage>

        {config.configFooter && config.configFooter.infoLinks && (
          <TpdInfo>
            <div className="tpd-red-row">
              <div className="tpd-blue"></div>
              <div className="tpd-red"></div>
              <div className="tpd-blue"></div>
            </div>
            <p className="visibility-data" onClick={() => setVisible(!visible)}>
              Dati societari
            </p>
            <div
              ref={companyDataRef}
              className={classNames("info", { visibility: !visible })}
            >
              <p>
                Unipol è un gruppo assicurativo, leader in Italia nei rami
                danni, in particolare nell’RCAuto. Fortemente attivo anche nei
                rami Vita, occupa una posizione di assoluta preminenza nella
                graduatoria nazionale dei gruppi assicurativi per raccolta
                diretta, e serve oltre 16 milioni di clienti. La Compagnia opera
                attraverso la più grande rete agenziale d’Italia, forte di circa
                2.000 agenzie assicurative e oltre 5.200 subagenzie distribuite
                sul territorio nazionale, tramite la quale offre una gamma
                completa di prodotti e servizi assicurativi, ed è leader
                indiscussa nella telematica assicurativa per il Ramo Auto.{" "}
                {Helpers.BrandNameHelper.brandName()} Assicurazioni ti offre
                soluzioni per assicurare la tua mobilità, la tua casa, il tuo
                lavoro, la tua protezione, il tuo risparmio. Scopri tutti i
                vantaggi a te riservati e costruisci la tua serenità!
              </p>
            </div>
          </TpdInfo>
        )}
      </ContainerDiv>
    )
  );
};

export default ReactMFE.PrimaryRemoteHOCFactory(
  React,
  styled,
  { CacheProvider },
  createCache
)(App);
